import React from "react";
import { Joyride } from "react-joyride";
import type { TooltipRenderProps } from "react-joyride";
import { Box, Button, Typography, Paper, LinearProgress, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import { useWalkthroughContext } from "./WalkthroughProvider";
import { useWalkthrough } from "../../hooks/useWalkthrough";

interface WalkthroughOverlayProps {
  navigateTo: (page: string, clientId?: string, params?: Record<string, unknown>) => void;
  janeClientId: string | null;
}

/** Glow animation CSS — targets the active tour element */
const GlowStyles: React.FC = () => (
  <style>{`
    @keyframes tour-glow {
      0%, 100% { box-shadow: 0 0 8px 4px rgba(14, 165, 233, 0.25); }
      50% { box-shadow: 0 0 24px 8px rgba(14, 165, 233, 0.55); }
    }
    [data-tour-active="true"] {
      animation: tour-glow 1.5s ease-in-out infinite !important;
      z-index: 100001;
    }
    /* Only force position:relative on elements that aren't already
       positioned; otherwise we clobber position:fixed/absolute/sticky
       (e.g. the email-fab Fab, bottom-right of viewport) and the element
       snaps back into normal flow during the tour. */
    [data-tour-active="true"]:not(.MuiFab-root):not([style*="position: fixed"]):not([style*="position: absolute"]):not([style*="position: sticky"]) {
      position: relative;
    }
  `}</style>
);

const CustomTooltip: React.FC<TooltipRenderProps> = ({
  continuous,
  index,
  isLastStep,
  size,
  step,
  backProps,
  closeProps,
  primaryProps,
  skipProps,
  tooltipProps,
}) => {
  const progress = ((index + 1) / size) * 100;
  const isClickStep = step.data?.stepType === "click";

  return (
    <Paper
      {...tooltipProps}
      elevation={8}
      sx={{
        maxWidth: 380,
        p: 0,
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: "#1a1f2e",
        border: "1px solid rgba(255,255,255,0.08)",
      }}
    >
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          height: 3,
          bgcolor: "rgba(255,255,255,0.05)",
          "& .MuiLinearProgress-bar": { bgcolor: "#0EA5E9" },
        }}
      />

      <Box sx={{ p: 2.5 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="caption" sx={{ color: "#0EA5E9", fontWeight: 600, letterSpacing: "0.05em" }}>
            {isClickStep ? (
              <Box component="span" display="flex" alignItems="center" gap={0.5}>
                <TouchAppIcon sx={{ fontSize: 14 }} />
                STEP {index + 1} OF {size}
              </Box>
            ) : (
              `STEP ${index + 1} OF ${size}`
            )}
          </Typography>
          <IconButton
            size="small"
            {...closeProps}
            // This tooltip pins a dark surface (#1a1f2e) on purpose — the tour
            // overlay is dark in BOTH app themes. So its foreground must be
            // frozen too: `text.secondary` follows the palette and in LIGHT
            // mode resolves to #475569, which is 2.17:1 here — the close button
            // effectively disappears. Freeze both, or freeze neither.
            sx={{ color: "rgba(255,255,255,0.7)", ml: 1, p: 0.5 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Typography variant="body2" sx={{ color: "#e2e8f0", lineHeight: 1.6 }}>
          {step.content}
        </Typography>
        {isClickStep && (
          <Typography
            variant="caption"
            sx={{ color: "#94a3b8", lineHeight: 1.5, display: "block", mt: 1.25 }}
          >
            Click the highlighted action to keep moving.
          </Typography>
        )}

        <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
          {/* Click steps: no Next/Back — user clicks the highlighted element */}
          {/* Explain steps: show Next/Back */}
          {!isClickStep && (
            <Box display="flex" gap={1} sx={{ ml: "auto" }}>
              {index > 0 && (
                <Button
                  size="small"
                  variant="outlined"
                  {...backProps}
                  sx={{
                    textTransform: "none",
                    borderColor: "rgba(255,255,255,0.15)",
                    color: "#94a3b8",
                    "&:hover": { borderColor: "rgba(255,255,255,0.3)" },
                  }}
                >
                  Back
                </Button>
              )}
              {continuous && (
                <Button
                  size="small"
                  variant="contained"
                  {...primaryProps}
                  sx={{
                    textTransform: "none",
                    bgcolor: "#0EA5E9",
                    "&:hover": { bgcolor: "#0284c7" },
                    fontWeight: 600,
                  }}
                >
                  {isLastStep ? "Finish" : "Next"}
                </Button>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

const IntroModal: React.FC<{
  onStart: () => void | Promise<void>;
  onClose: () => void;
}> = ({
  onStart,
  onClose,
}) => {
  const [starting, setStarting] = React.useState(false);

  const handleStart = async () => {
    if (starting) return;
    setStarting(true);
    try {
      await onStart();
    } finally {
      setStarting(false);
    }
  };

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 100000,
        bgcolor: "rgba(0, 0, 0, 0.82)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Paper
        elevation={12}
        sx={{
          maxWidth: 520,
          width: "100%",
          borderRadius: 3,
          p: 3,
          bgcolor: "#161b29",
          border: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <Typography variant="overline" sx={{ color: "#0EA5E9", fontWeight: 700, letterSpacing: "0.08em" }}>
          Demo Tour
        </Typography>
        <Typography variant="h5" sx={{ color: "#f8fafc", fontWeight: 700, mt: 0.5, mb: 1.25 }}>
          Walk the real demo workflow
        </Typography>
        <Typography variant="body2" sx={{ color: "#cbd5e1", lineHeight: 1.7, mb: 2 }}>
          This resets the demo and walks through the main locator flow from a clean state.
        </Typography>
        <Box component="ul" sx={{ color: "#e2e8f0", pl: 2.5, mb: 3, mt: 0 }}>
          <li>Open Jane and move her into Touring</li>
          <li>Launch Send List and choose a floor plan</li>
          <li>Preview and send the demo email</li>
        </Box>
        <Box display="flex" justifyContent="space-between" gap={1.5}>
          <Button
            variant="text"
            onClick={onClose}
            disabled={starting}
            sx={{ textTransform: "none", color: "#94a3b8" }}
          >
            Not now
          </Button>
          <Button
            variant="contained"
            onClick={handleStart}
            disabled={starting}
            startIcon={starting ? <TouchAppIcon sx={{ fontSize: 16 }} /> : undefined}
            sx={{
              textTransform: "none",
              bgcolor: "#0EA5E9",
              "&:hover": { bgcolor: "#0284c7" },
              fontWeight: 700,
            }}
          >
            {starting ? "Resetting..." : "Start Tour"}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export const WalkthroughOverlay: React.FC<WalkthroughOverlayProps> = ({
  navigateTo,
  janeClientId,
}) => {
  const {
    isWalkthroughActive,
    walkthroughStage,
    beginWalkthrough,
    endWalkthrough,
    cancelWalkthrough,
    registerNavigate,
    registerFakeSend,
  } = useWalkthroughContext();

  const {
    stepIndex,
    isRunning,
    isStepReady,
    steps,
    start,
    reset,
    handleEvent,
  } = useWalkthrough({
    navigateTo,
    janeClientId,
    onComplete: endWalkthrough,
    onCancel: cancelWalkthrough,
  });

  // Register navigateTo with the provider
  React.useEffect(() => {
    registerNavigate(navigateTo);
  }, [navigateTo, registerNavigate]);

  // Register fake send handler: navigate to client tracking
  React.useEffect(() => {
    registerFakeSend(() => {
      setTimeout(() => {
        navigateTo("clients", janeClientId || undefined, { tab: "searches" });
      }, 100);
    });
  }, [navigateTo, janeClientId, registerFakeSend]);

  // Sync context → hook
  React.useEffect(() => {
    if (walkthroughStage === "tour") {
      start();
    } else {
      reset();
    }
  }, [walkthroughStage, start, reset]);

  if (!isWalkthroughActive) return null;

  return (
    <>
      <GlowStyles />
      {walkthroughStage === "intro" && (
        <IntroModal
          onStart={beginWalkthrough}
          onClose={cancelWalkthrough}
        />
      )}
      {walkthroughStage === "tour" && (
        <Joyride
          steps={steps}
          stepIndex={stepIndex}
          run={isRunning && isStepReady}
          continuous
          onEvent={handleEvent}
          tooltipComponent={CustomTooltip}
          options={{
            zIndex: 100000,
            overlayColor: "rgba(0, 0, 0, 0.7)",
            skipBeacon: true,
            spotlightPadding: 12,
            targetWaitTimeout: 8000,
            primaryColor: "#0EA5E9",
          }}
        />
      )}
    </>
  );
};
